import { useCallback, useRef } from "react";

interface SegmentedControlProps {
  label: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (value: string) => void;
}

/**
 * Segmented toggle with ARIA radiogroup semantics.
 *
 * - Container uses `role="radiogroup"` + `aria-label`.
 * - Each option uses `role="radio"` + `aria-checked`.
 * - Arrow keys move focus AND selection (roving tabindex pattern).
 */
export function SegmentedControl({
  label,
  options,
  value,
  onChange,
}: SegmentedControlProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const base =
    "flex-1 border-0 text-[12px] font-semibold py-1.5 rounded-[7px] transition-all duration-100 cursor-pointer focus-visible:ring-2 focus-visible:ring-tide focus-visible:ring-offset-1";
  const active = `${base} bg-tide text-white`;
  const inactive = `${base} bg-transparent text-ink-4 hover:text-ink-3`;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const idx = options.findIndex((o) => o.value === value);
      let next: number | null = null;

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        next = (idx + 1) % options.length;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        next = (idx - 1 + options.length) % options.length;
      } else if (e.key === "Home") {
        e.preventDefault();
        next = 0;
      } else if (e.key === "End") {
        e.preventDefault();
        next = options.length - 1;
      }

      if (next !== null) {
        onChange(options[next].value);
        // Move focus to the newly selected button
        const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>(
          '[role="radio"]',
        );
        buttons?.[next]?.focus();
      }
    },
    [options, value, onChange],
  );

  return (
    <div className="mb-[18px]">
      <label className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
        {label}
      </label>
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label={label}
        className="flex bg-paper-2 rounded-[9px] p-[3px]"
      >
        {options.map((opt) => {
          const selected = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              tabIndex={selected ? 0 : -1}
              className={selected ? active : inactive}
              onClick={() => onChange(opt.value)}
              onKeyDown={handleKeyDown}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
